import { useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CONVERSATION_COLlECTION, MESSAGE_COLlECTION } from "@fb/const";
import { db } from "@fb/db";
import {
  DocumentData,
  collection,
  orderBy,
  query,
  where,
  WithFieldValue,
  QueryDocumentSnapshot,
  SnapshotOptions,
  FirestoreDataConverter,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";

import { RootState } from "@app/store";

import { setLoading, setMessages } from "@features/messages/slice";

import { AssistantMessageType } from "@components/AssistantChat/type";

const postConverter: FirestoreDataConverter<any> = {
  toFirestore(post: WithFieldValue<any>): DocumentData {
    return { author: post.author, title: post.title };
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): any {
    const data = snapshot.data(options);
    return {
      id: snapshot.id,
      ...data,
    };
  },
};

export const useFetchMessages = (conversationId: string) => {
  const dispatch = useDispatch();
  const messages = useSelector(
    (state: RootState) => state.messages.messages[conversationId] || []
  );
  const loading = useSelector(
    (state: RootState) => state.messages.loading[conversationId] || false
  );
  const isFetched = useSelector(
    (state: RootState) => state.messages.fetchedConversations[conversationId] || false
  );
  const userId = useSelector((state: RootState) => state.auth.userInfo?.user?.id);

  const fetchMessages = useCallback(async () => {
    if (!conversationId || !userId || isFetched) {
      return;
    }

    try {
      dispatch(setLoading({ conversationId, loading: true }));

      const conversationRef = doc(db, CONVERSATION_COLlECTION, conversationId);
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) {
        throw new Error("Conversation not found");
      }

      const conversationData = conversationSnap.data();
      if (!conversationData?.participants?.includes(userId)) {
        throw new Error("User is not a participant in this conversation");
      }

      const messagesQuery = query(
        collection(db, MESSAGE_COLlECTION).withConverter(postConverter),
        where("conversationId", "==", conversationId),
        orderBy("date", "asc")
      );

      const querySnapshot = await getDocs(messagesQuery);
      const fetchedMessages = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toMillis() || Date.now(),
      })) as AssistantMessageType[];

      dispatch(setMessages({ conversationId, messages: fetchedMessages }));
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      dispatch(setLoading({ conversationId, loading: false }));
    }
  }, [conversationId, dispatch, isFetched, userId]);

  return { messages, loading, fetchMessages, isFetched };
};
